import clsx from 'clsx';
import React from 'react';
import type { IconColor, IconName, IconSize } from '../../icons/types';
import { VALIDATION_CONFIG } from '../FormValidationMessage/validationConfig';
import Icon from '../Icon/Icon';
import useInputText from './useInputText';


type InputTextProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'value' | 'placeholder' | 'type' | 'min' | 'max'
> & {
  className?: string;
  disabled?: boolean;
  icon?: IconName;
  iconAriaLabel?: string;
  iconColor?: IconColor;
  iconLeft?: boolean;
  iconSize?: IconSize;
  id?: string;
  max?: number | string;
  min?: number | string;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickIcon?: (...args: unknown[]) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  referer?: React.Ref<HTMLInputElement>;
  type?: string;
  /** Decorative-only flag for the inline validation icon. Usually injected by `FormField`. */
  validationState?: 'error' | 'success';
  value?: string;
  variant?: 'default' | 'focus';
};

const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      'aria-label': ariaLabel,
      className,
      disabled = false,
      icon,
      iconAriaLabel,
      iconColor,
      iconLeft = false,
      iconSize = 'lg',
      name,
      onChange = () => null,
      onClickIcon,
      onKeyDown = () => {},
      placeholder,
      referer,
      type = 'text',
      validationState,
      value,
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const { refInput, getIconAriaLabelValue, isIconClickable } = useInputText({
      disabled,
      icon,
      iconAriaLabel,
      onClickIcon,
      ref,
      referer,
    });

    const iconElement = icon ? <Icon name={icon} size={iconSize} color={iconColor} /> : null;
    const decorativeIcon = icon ? (
      <div className="input-text__icon" aria-hidden="true">
        {iconElement}
      </div>
    ) : null;

    return (
      <div
        className={clsx(
          'input-text',
          `input-text--${variant}`,
          className,
          { 'input-text--icon': !!icon },
          { 'input-text--icon-left': iconLeft },
          { 'input-text--has-validation-icon': !!validationState },
        )}
      >
        <div className="input-text__input-container">
          <input
            name={name}
            className={clsx('input-text__input', { 'input-text__input--disabled': disabled })}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            ref={refInput}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            {...props}
          />
          {icon && isIconClickable ? (
            <button
              type="button"
              className="input-text__icon input-text--icon-click"
              onClick={onClickIcon}
              aria-label={getIconAriaLabelValue}
            >
              {iconElement}
            </button>
          ) : decorativeIcon}
          {validationState && (
            <div
              className={`input-text__validation-icon input-text__validation-icon--${validationState}`}
              aria-hidden="true"
            >
              {/* color="inherit" makes the icon follow the wrapper's state color
                  (--ui-color-notification-*) so it matches FormValidationMessage. */}
              <Icon name={VALIDATION_CONFIG[validationState].inlineIcon} size="md" color="inherit" />
            </div>
          )}
        </div>
      </div>
    );
  }
);

InputText.displayName = 'InputText';

// Opt in to receiving `validationState` from FormField (used to render the inline
// validation icon). FormField only injects it into children that declare this flag.
(InputText as typeof InputText & { supportsValidationState: boolean }).supportsValidationState = true;

export default InputText;
